import BuildGlobalColorInput from "@builder/components/general/BuildGlobalColorInput";
import ColorInput from "@builder/components/general/ColorInput";
import OliverText from "@builder/components/general/OliverText";
import Toggle from "@builder/components/general/Toggle";
import {
    addBuildGlobalColor,
    addFontToAttributes,
    deleteBuildGlobalColor,
    overrideBuildGlobalColors,
    removeBuildSettingKey,
    setFontRoots,
    updateBuildGlobalColor
} from "@builder/store/slices/BuilderSlice/builderSlice";
import {
    setContainerOutline,
    setCustomCanvasBgColor
    // setDarkMode
} from "@builder/store/slices/systemUISlice";
import { useAppDispatch, useAppSelector } from "@builder/store/store";
import SidebarCollapse from "../SidebarCollapse";
// import SelectInput from "@builder/components/general/SelectInput";
import SelectInputFonts from "@builder/components/general/SelectInputFonts";
import { ReorderableList } from "@builder/components/general/ReorderableListDND/ReorderableList";
import availableFonts from "@builder/fonts/availableFonts";
// import { getCurrentLanguage, handleLanguageChange, languageMap } from "@builder/utils/lokalize";
// import { useState } from "react";
import { useTranslation } from "react-i18next";
import "./style.scss";
import { useMemo } from "react";
import { v4 } from "uuid";
import {
    addBuildSettingsKey,
    BuildSettingsKey,
    deleteBuildSettingsKey,
    NewBuildSettingKey,
    updateBuildSettingsKey
} from "@builder/store/slices/buildSettingsKeys/buildSettingsKeySlice";

const fontNamesObject: Record<string, string> = availableFonts.reduce((acc, font) => ({ ...acc, [font.name]: font.name }), {});

const Settings = () => {
    const dispatch = useAppDispatch();

    const systemUI = useAppSelector(state => state.systemUI);
    const buildGlobalColors = useAppSelector(state => state.builder.build.extraAttributes?.buildGlobalColors);
    const fontsLoaded = useAppSelector(state => state.builder.build.extraAttributes?.fonts);

    const build = useAppSelector(state => state.builder.build);
    const buildSettingsKeys = useAppSelector(state => state.buildSettingsKey);

    const partialBSK: Partial<BuildSettingsKey> = useMemo(
        () => ({
            buildId: build.id,
            organizationId: build.companyId,
            companyId: build.companyId,
            dataType: "color"
        }),
        [build?.id, build?.companyId]
    );

    const updateBSKColor = async (update: Partial<BuildSettingsKey>, globalColorId: string, bsk?: Partial<BuildSettingsKey>) => {
        if (bsk?.key && (update.label || bsk.label)) {
            dispatch(updateBuildSettingsKey({ key: bsk.key, update }));
            dispatch(
                updateBuildGlobalColor({
                    id: globalColorId,
                    data: { name: update.label || bsk.label || "", color: bsk.key }
                })
            );
        } else {
            if (update.label) {
                const newBSKKey = `buildSettingsKey.${v4()}`;
                dispatch(
                    addBuildSettingsKey({
                        ...partialBSK,
                        ...update,
                        key: newBSKKey
                    } as NewBuildSettingKey)
                );
                dispatch(
                    updateBuildGlobalColor({
                        id: globalColorId,
                        data: { name: update.label, color: newBSKKey }
                    })
                );
            }
        }
    };

    const deleteBSKColor = async (bsk: Partial<BuildSettingsKey>, globalColorId: string, deleteGB?: boolean) => {
        if (bsk.key && bsk.label && bsk.placeholder) {
            dispatch(deleteBuildSettingsKey(bsk.key));
            dispatch(removeBuildSettingKey(bsk.key));
            if (deleteGB) {
                dispatch(deleteBuildGlobalColor({ key: globalColorId, replace: bsk.placeholder }));
            } else {
                dispatch(
                    updateBuildGlobalColor({
                        id: globalColorId,
                        data: { name: bsk.label, color: bsk.placeholder }
                    })
                );
            }
        }
    };

    const { t } = useTranslation();

    const handleReorderGlobalColors = (reorderedColors: { id: string; name: string; color: string }[]) => {
        dispatch(overrideBuildGlobalColors(reorderedColors));
    };

    const fontStyleCallback = (fontName: string) => {
        const selectedFont = availableFonts.find(font => font.name === fontName);
        if (!selectedFont)
            return {
                name: "Poppins"
            };

        dispatch(addFontToAttributes({ font: selectedFont, asRoot: true }));
        dispatch(setFontRoots(selectedFont));
    };

    //Language settings
    // const [currentLanguage, setCurrentLanguage] = useState<string>(getCurrentLanguage().value);
    // const langCallback = (value: string) => {
    //     handleLanguageChange(value);
    //     setCurrentLanguage(value);
    // };

    return (
        <div className="settings-wrapper">
            <div className="settings-body">
                <SidebarCollapse title="Builder UI" key={"BuilderUI"} open={true}>
                    {/* <Toggle
                        label={systemUI.isDarkMode ? "Turn off dark mode" : "Turn on dark mode"}
                        checked={systemUI.isDarkMode || false}
                        callback={checked => {
                            dispatch(setDarkMode(checked));
                        }}
                    /> */}
                    <Toggle
                        label={"Show container outline"}
                        checked={systemUI.containerOutline || false}
                        callback={checked => {
                            dispatch(setContainerOutline(checked));
                        }}
                    />
                    <div>
                        <ColorInput
                            title="Bg canvas color"
                            color={systemUI.customCanvasBG}
                            callback={color => {
                                dispatch(setCustomCanvasBgColor(color));
                            }}
                            options={{ disableGlobalColors: true }}
                        />
                        <button
                            className="style-button-clear"
                            onClick={() => {
                                dispatch(setCustomCanvasBgColor(undefined));
                            }}
                        >
                            {t("settings.clear")}
                        </button>
                    </div>

                    {/* <SelectInput title="Language" layout="column" value={currentLanguage} options={languageMap} callback={langCallback} noTranslate /> */}
                </SidebarCollapse>
                <SidebarCollapse title="Build settings" key={"build-settings"} open={true}>
                    <SelectInputFonts
                        fonts={fontNamesObject}
                        callback={fontStyleCallback}
                        value={(fontsLoaded && fontsLoaded[0].name) || "Poppins"}
                        title="Text font"
                        layout="column"
                    />
                    {/* <TextSettings /> */}
                </SidebarCollapse>
                <SidebarCollapse title="Global colors" key={"global-colors"} open={true}>
                    <OliverText text="Set global colors" size="xs" translatePrefix="settings." />
                    {buildGlobalColors && (
                        <ReorderableList
                            items={buildGlobalColors}
                            onReorder={handleReorderGlobalColors}
                            itemKey={colorObj => colorObj.id}
                            showDragHandle={true}
                            renderItem={(colorObj, _index, _dragProps) => (
                                <BuildGlobalColorInput
                                    key={colorObj.id}
                                    id={colorObj.id}
                                    color={colorObj.color}
                                    name={colorObj.name}
                                    buildSettingsKey={buildSettingsKeys?.[colorObj.color]}
                                    callback={(color, name) => {
                                        dispatch(
                                            updateBuildGlobalColor({
                                                id: colorObj.id,
                                                data: { name, color }
                                            })
                                        );
                                    }}
                                    deleteCallback={() => {
                                        dispatch(deleteBuildGlobalColor({ key: colorObj.id }));
                                    }}
                                    bskCallback={updateBSKColor}
                                    deleteBSK={deleteBSKColor}
                                />
                            )}
                        />
                    )}
                    <button className="add" onClick={() => dispatch(addBuildGlobalColor())}>
                        {t("generalComponents.button.Add Global Color")}
                    </button>
                </SidebarCollapse>
            </div>
        </div>
    );
};

export default Settings;
